import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, unstable_batchedUpdates } from "react-dom";
import { v4 as uuidV4 } from 'uuid';
import classNames from "classnames";
import {
	closestCenter,
	pointerWithin,
	rectIntersection,
	CollisionDetection,
	DndContext,
	DragOverlay,
	DropAnimation,
	getFirstCollision,
	MouseSensor,
	TouchSensor,
	UniqueIdentifier,
	useSensors,
	useSensor,
	MeasuringStrategy,
	defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	verticalListSortingStrategy,
	horizontalListSortingStrategy
} from "@dnd-kit/sortable";

import '../../../../../../scss/live-event/base/modules/accordion.scss';
import './editor-accordion.scss';
import { useAppDispatch, useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { setActiveAccordionContainer, updateHomepageModule } from "../../../../../../store/actions/admin/create-event";
import { BasicLangingModuleProps } from "../editor-main";
import { EditorSizes } from "../../../../../../types/template-layouts";
import { getStylingOverrides } from "../../../../../live-event/utils";
import Controls from "../../../../../admin/create/session/editor/moduleControls/controls";
import { AccordionContainersType, AccordionItem, AccordionColumns, AccordionItemUpdate, LanguagesAbbr, AccordionContent, ColorKeys, TextOverrides, ContentEditors, FeatureFlagsEnum } from "../../../../../../types/working-model";
import SortableItem from "./sortable-item";
import DroppableContainer from "./accordion-container/droppable-container";
import AccordionContainer from "./accordion-container/accordion-container";
import Item from "./accordion-item/item";
import { GetDefaultTranslateString } from "../../../../../../store/utils/create-event";
import usePrevious from "../../../../../../utils/use-previous";
import store from "../../../../../../store/main";
import { getDefaultModuleAlignment, getTextValue, updateTranslateKey } from "../../../../../../utils/utils";
import { useTranslate } from "../../../../../../i18n/useTranslationModules";
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import { EContentColorOverrideNames } from "../../../../../general-ui/edit-section-colors/edit-section-colors-modal";
import { InitialControlsDisplay } from "../../../session/editor/moduleControls/controls-wrapped";
import { OptionalComponent } from "../../../../../../utils/optional-component";
import EditableTextDraftQuill from "../../../../../general-ui/editable-text/editable-text-draft-quill";
import useTranslationRoute from "../../../../../live-event/hooks/use-translation-route";


const MAX_CONTAINERS = 2;

const dropAnimation: DropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: {
			active: {
				opacity: "0.5"
			}
		}
	})
};

const createDefaultItem = (baseLanguage: LanguagesAbbr, language: LanguagesAbbr): AccordionItem => {
	return {
		id: uuidV4(),
		title: updateTranslateKey({
			translateString: GetDefaultTranslateString('Question'),
			input: 'Question',
			baseLanguage,
			language,
		}),
		description: updateTranslateKey({
			translateString: GetDefaultTranslateString('Answer'),
			input: 'Answer',
			baseLanguage,
			language,
		}),
	};
};

const EditorAccordion: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({
	page_module,
	template,
	language,
	baseLanguage,
}, ref) => {

	const focusedContainer = useTypedSelector(state => state.CreateEventReducer.accordion.focusedContainer);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const moduleUpdates = useTypedSelector(state => state.CreateEventReducer.moduleUpdates);
	const savingEvent = useTypedSelector(state => state.CreateEventReducer.saving);
	const contentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);

	const accordionModuleFlagOn = featureFlags[FeatureFlagsEnum.accordion_module];

	const [pageModule, setPageModule] = useState(page_module);

	const initialItems: AccordionColumns = useMemo(() => {
		const columns: AccordionColumns = page_module?.content?.columns;
		// if there are no items, start with a default item
		if (!columns || !Object.entries(columns).some(column => !!column[1].length)) {
			return { 1: [createDefaultItem(baseLanguage, language)] };
		}
		return columns;
	}, [baseLanguage, language, page_module?.content?.columns]);

	const containerOrder = page_module?.content?.container_order || Object.keys(initialItems).map(Number);
	const columns = Object.keys(initialItems).length;
	const styling_overrides = pageModule?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);

	const isMobile = editorSize === EditorSizes.mobile;
	const isTablet = editorSize === EditorSizes.tablet;
	const vertical = isMobile || isTablet;

	const dispatch = useAppDispatch();

	const [items, setItems] = useState<AccordionColumns>(initialItems);
	const [containers, setContainers] = useState<AccordionContainersType>(containerOrder);
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
	const [isEdited, setIsEdited] = useState(false); // this prevents triggering the update useEffect on initial render
	const [clonedItems, setClonedItems] = useState<AccordionColumns | null>(null);
	const [clonedContainers, setClonedContainers] = useState<AccordionContainersType | null>(null);

	const lastOverId = useRef<UniqueIdentifier | null>(null);
	const recentlyMovedToNewContainer = useRef(false);
	const isSortingContainer = activeId ? containers.includes(activeId) : false;

	const pageModuleId = pageModule?.id;

	const prevModulesUpdates = usePrevious(moduleUpdates);

	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, "homepage"]);

	const content: AccordionContent = page_module?.content;
	const { custom_heading, description, title } = getTextValue(content, language);

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							language,
							baseLanguage,
						}),
					},
					is_edited: true,
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const prevTranslationUpdates = usePrevious(translationUpdates);

	// this hook updates local state items so when we save the data into the db we re-render
	// the items so we can properly render translations
	useEffect(() => {
		if (
			page_module
			&& (
				prevModulesUpdates !== moduleUpdates)
			|| prevTranslationUpdates !== translationUpdates
		) {
			setIsEdited(false);
			setPageModule(page_module);
			setItems(initialItems);
			setContainers(containerOrder);
		}
	}, [
		baseLanguage,
		containerOrder,
		initialItems,
		language,
		moduleUpdates,
		page_module,
		prevModulesUpdates,
		prevTranslationUpdates,
		translationUpdates,
	]);

	// this hook triggers the database update whenever items or containers changes
	useEffect(() => {
		if (isEdited && pageModuleId) {
			// find module by directly calling store.getState
			// otherwise we'll get an infinite loop if we try and pass page_module to the dep array
			const workingEvent = store.getState().CreateEventReducer.workingEvent;
			const moduleList = [
				...workingEvent?.homepage?.modules ?? [],
				...workingEvent?.homepage?.post_register_home_modules ?? [],
				...workingEvent?.custom_pages?.map(page => page.modules)?.flat() ?? []
			];
			const module = moduleList?.find((module) => module?.id === pageModuleId);
			if (module) {
				const updatedModule = {
					...module,
					content: {
						...module.content,
						columns: items,
						container_order: containers,
					},
				};
				dispatch(updateHomepageModule(updatedModule));
			}
		}
	}, [containers, dispatch, isEdited, items, pageModuleId]);

	/**
	 * Custom collision detection strategy optimized for multiple containers
	 *
	 * - First, find any droppable containers intersecting with the pointer.
	 * - If there are none, find intersecting containers with the active draggable.
	 * - If there are no intersecting containers, return the last matched intersection
	 *
	 */
	const collisionDetectionStrategy: CollisionDetection = useCallback(
		(args) => {
			if (activeId && activeId in items) {
				return closestCenter({
					...args,
					droppableContainers: args.droppableContainers.filter(
						(container) => container.id in items
					)
				});
			}

			// Start by finding any intersecting droppable
			const pointerIntersections = pointerWithin(args);
			const intersections =
				pointerIntersections.length > 0
					? // If there are droppables intersecting with the pointer, return those
					pointerIntersections
					: rectIntersection(args);
			let overId = getFirstCollision(intersections, "id");

			if (overId != null) {

				if (overId in items) {
					const containerItems = items[Number(overId)];

					// If a container is matched and it contains items
					if (containerItems.length > 0) {
						// Return the closest droppable within that container
						overId = closestCenter({
							...args,
							droppableContainers: args.droppableContainers.filter((container) => {
								if (container.id !== overId && containerItems.find(ci => ci.id === container.id)) {
									return true;
								}
								return false;
							})
						})[0]?.id;
					}
				}

				lastOverId.current = overId;

				return [{ id: overId }];
			}

			// When a draggable item moves to a new container, the layout may shift
			// and the `overId` may become `null`. We manually set the cached `lastOverId`
			// to the id of the draggable item that was moved to the new container, otherwise
			// the previous `overId` will be returned which can cause items to incorrectly shift positions
			if (recentlyMovedToNewContainer.current) {
				lastOverId.current = activeId;
			}

			// If no droppable is matched, return the last match
			return lastOverId.current ? [{ id: lastOverId.current }] : [];
		},
		[activeId, items]
	);

	const sensors = useSensors(
		useSensor(MouseSensor, {
			activationConstraint: {
				distance: focusedContainer ? Infinity : 10,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				distance: focusedContainer ? Infinity : 10,
			},
		}),
	);
	const findContainer = useCallback((id: UniqueIdentifier) => {
		if (id in items) {
			return id;
		}

		return Object.keys(items).find((key) => items[Number(key)].find(item => item.id === id));
	}, [items]);

	const getIndex = useCallback((id: UniqueIdentifier) => {
		const container = findContainer(id);

		if (!container) {
			return -1;
		}

		const index = items[Number(container)].findIndex(item => item.id === id);

		return index;
	}, [findContainer, items]);

	const onDragCancel = () => {
		if (clonedItems && clonedContainers) {
			// Reset items to their original state in case items have been
			// Dragged across containers
			setItems(clonedItems);
			setContainers(clonedContainers);
		}

		setActiveId(null);
		setClonedItems(null);
		setClonedContainers(null);
	};

	useEffect(() => {
		requestAnimationFrame(() => {
			recentlyMovedToNewContainer.current = false;
		});
	}, [items]);

	const allItemsLength = useMemo(() => {
		return Object.values(items).flat().length;
	}, [items]);

	const removeItem = useCallback((itemId: UniqueIdentifier, containerId: UniqueIdentifier) => {
		setIsEdited(true);
		// update the items list
		const updatedContainer = items[Number(containerId)].filter((item) => item.id !== itemId);
		const updatedItems = {
			...items,
			[containerId]: updatedContainer
		};
		// set the new items
		setItems(updatedItems);
		setActiveId(null);
	}, [items]);

	const handleContentBlur = useCallback(({ itemId, containerId, title, description }: AccordionItemUpdate) => {
		// update item content
		setItems(prev => {
			return {
				...prev,
				[containerId]: prev[Number(containerId)].map(item => {
					if (item.id !== itemId) {
						return item;
					}

					return {
						...item,
						title: updateTranslateKey({
							translateString: item.title,
							input: title,
							language,
							baseLanguage,
						}),
						description: updateTranslateKey({
							translateString: item.description,
							input: description,
							language,
							baseLanguage,
						}),
					};
				})
			};
		});

		if (!isEdited) {
			setIsEdited(true);
		}
	}, [baseLanguage, isEdited, language]);

	const handleAddItem = (containerId: UniqueIdentifier) => () => {
		setIsEdited(true);
		setItems(prev => ({
			...prev,
			[containerId]: [
				...prev[Number(containerId)],
				createDefaultItem(baseLanguage, language),
			]
		}));
	};

	const renderSortableItems = useCallback((containerId: UniqueIdentifier) => {
		return items[Number(containerId)].map((value, index) => {
			return (
				<SortableItem
					disabled={isSortingContainer}
					key={value.id}
					id={value.id}
					index={index}
					handle={false}
					value={value}
					containerId={containerId}
					getIndex={getIndex}
					hideRemoveButton={allItemsLength === 1}
					onRemove={(removedItemId) => removeItem(removedItemId, containerId)}
					onContentBlur={handleContentBlur}
					module={pageModule}
				/>
			);
		});
	}, [allItemsLength, getIndex, handleContentBlur, isSortingContainer, items, pageModule, removeItem]);

	const atMaxColumns = containers.length >= MAX_CONTAINERS;

	if (!accordionModuleFlagOn) return null;
	return (
		<OptionalComponent display={pageModule.is_on}>
			<section
				ref={ref}
				className={classNames(
					"editor-wrapper accordion",
					backgroundColor,
					template
				)}
			>
				<div className={classNames('accordion-module-overlay', { 'show': savingEvent })} />
				<Controls
					openContentModal={() => {
						if (atMaxColumns) {
							return handleRemoveColumn();
						}
						handleAddColumn();
					}}
					module={pageModule}
					contentButtonText={atMaxColumns ? "Remove column" : "Add column"}
					baseLanguage={baseLanguage}
					language={language}
					display={{ ...InitialControlsDisplay, editLayout: false }}
					disabled={savingEvent}
				/>

				<div className="module-container" style={backgroundImg}>
					<EditableTextSlim
						tagType="p"
						className="accordion-custom_heading"
						elementClass="event-caption-1"
						value={t(`${translationRoute}:accordion.${pageModule.id}.custom_heading`, custom_heading)}
						defaultColor={ColorKeys.secondaryAccentColor}
						onChange={handleTextChange("custom_heading")}
						onOverrideChange={handleStyleOverrideChange("custom_heading")}
						elementName={EContentColorOverrideNames.custom_heading}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
					/>
					<EditableTextSlim
						tagType="h2"
						className="accordion-title"
						elementClass="evt-heading-2"
						value={t(`${translationRoute}:accordion.${pageModule.id}.title`, title)}
						defaultColor={ColorKeys.bodyTextColor}
						onChange={handleTextChange("title")}
						onOverrideChange={handleStyleOverrideChange("title")}
						elementName={EContentColorOverrideNames.title}
						colorOverride={styling_overrides?.content?.title?.color}
					/>
					<EditableTextDraftQuill
						className="accordion-description"
						value={t(`${translationRoute}:accordion.${pageModule.id}.description`, description)}
						onChange={handleTextChange("description")}
						defaultColor={ColorKeys.bodyTextColor}
						onOverrideChange={handleStyleOverrideChange("description")}
						elementName={EContentColorOverrideNames.description}
						styleOverrides={styling_overrides?.content?.description}
						useNew={contentEditor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
					/>

					<DndContext
						sensors={sensors}
						collisionDetection={collisionDetectionStrategy}
						measuring={{
							droppable: {
								strategy: MeasuringStrategy.Always
							}
						}}
						onDragStart={({ active }) => {
							setActiveId(active.id);
							setClonedItems(items);
							setClonedContainers(containers);
						}}
						onDragOver={({ active, over }) => {
							const overId = over?.id;

							if (overId == null || active.id in items) {
								return;
							}

							const overContainer = findContainer(overId);
							const activeContainer = findContainer(active.id);

							if (!overContainer || !activeContainer) {
								return;
							}

							if (activeContainer !== overContainer) {
								setIsEdited(true);
								setItems((items) => {
									const activeItems = items[Number(activeContainer)];
									const overItems = items[Number(overContainer)];
									const overIndex = overItems.findIndex(item => item.id === overId);
									const activeIndex = activeItems.findIndex(item => item.id === active.id);

									let newIndex: number;

									if (overId in items) {
										newIndex = overItems.length + 1;
									} else {
										const isBelowOverItem =
											over &&
											active.rect.current.translated &&
											active.rect.current.translated.top >
											over.rect.top + over.rect.height;

										const modifier = isBelowOverItem ? 1 : 0;

										newIndex =
											overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
									}

									recentlyMovedToNewContainer.current = true;

									return {
										...items,
										[activeContainer]: items[Number(activeContainer)].filter(
											(item) => item.id !== active.id
										),
										[overContainer]: [
											...items[Number(overContainer)].slice(0, newIndex),
											items[Number(activeContainer)][activeIndex],
											...items[Number(overContainer)].slice(
												newIndex,
												items[Number(overContainer)].length
											)
										]
									};
								});
							}
						}}
						onDragEnd={({ active, over }) => {

							dispatch(setActiveAccordionContainer(null));

							if (active.id in items && over?.id) {
								setIsEdited(true);
								setContainers((containers) => {
									const activeIndex = containers.indexOf(active.id);
									const overIndex = containers.indexOf(over.id);

									return arrayMove(containers, activeIndex, overIndex);
								});
							}

							const activeContainer = findContainer(active.id);

							if (!activeContainer) {
								setActiveId(null);
								return;
							}

							const overId = over?.id;

							if (overId == null) {
								setActiveId(null);
								return;
							}

							const overContainer = findContainer(overId);

							if (overContainer) {
								const activeIndex = items[Number(activeContainer)].findIndex(item => item.id === active.id);
								const overIndex = items[Number(overContainer)].findIndex(item => item.id === overId);

								if (activeIndex !== overIndex) {
									setIsEdited(true);
									setItems((items) => ({
										...items,
										[overContainer]: arrayMove(
											items[Number(overContainer)],
											activeIndex,
											overIndex
										)
									}));
								}
							}
							setActiveId(null);
						}}
						onDragCancel={onDragCancel}
					>
						<div
							className={classNames("sortable-context-container accordion-container", { vertical }, { 'single-column': containers.length === 1 })}
						>
							<SortableContext
								items={containers}
								strategy={
									vertical
										? verticalListSortingStrategy
										: horizontalListSortingStrategy
								}
							>
								{containers.map((containerId) => (
									<DroppableContainer
										key={containerId}
										id={containerId}
										columns={columns}
										items={items[Number(containerId)]}
										containers={containers}
										containerId={containerId}
										onRemove={containers?.length === 1 ? undefined : () => handleRemove(containerId)}
										vertical={vertical}
									>
										<SortableContext items={items[Number(containerId)]} strategy={verticalListSortingStrategy/* strategy */}>
											{renderSortableItems(containerId)}
										</SortableContext>
										<button
											className="clear no-style add-accordion-item-btn"
											onClick={handleAddItem(containerId)}
										>+ Add</button>
									</DroppableContainer>
								))}
							</SortableContext>
						</div>
						{createPortal(
							<DragOverlay dropAnimation={dropAnimation}>
								{activeId
									? containers.includes(activeId)
										? renderContainerDragOverlay(activeId)
										: renderSortableItemDragOverlay(activeId)
									: null}
							</DragOverlay>,
							document.body
						)}
					</DndContext>
				</div>
			</section>
		</OptionalComponent>
	);

	function renderSortableItemDragOverlay(id: UniqueIdentifier) {
		let item: AccordionItem = { id, title: GetDefaultTranslateString(), description: GetDefaultTranslateString() };
		Object.values(items).forEach(value => {
			const matchedItem = value.find((item: AccordionItem) => item.id === id);
			if (matchedItem) {
				item = matchedItem;
			}
		});

		const containerId = findContainer(id) as UniqueIdentifier;

		return (
			<Item
				value={item}
				handle={false}
				dragOverlay
				containerId={containerId}
			/>
		);
	}

	function renderContainerDragOverlay(containerId: UniqueIdentifier) {
		return (
			<AccordionContainer
				columns={columns}
				shadow
				unstyled={false}
				containers={containers}
				containerId={containerId}
				vertical={vertical}
			>
				{items[Number(containerId)].map(item => (
					<Item
						key={item.id}
						value={item}
						handle={false}
						containerId={containerId}
					/>
				))}
			</AccordionContainer>
		);
	}

	function handleRemove(containerID: UniqueIdentifier) {
		// remove container from containers list order
		setIsEdited(true);

		unstable_batchedUpdates(() => {
			setContainers((containers) => {
				if (containers?.length === 1) {
					return containers;
				}
				return containers.filter((id) => id !== containerID);
			});
		});
	}

	function handleAddColumn() {
		const newContainerId = getNextContainerId();
		setIsEdited(true);

		unstable_batchedUpdates(() => {
			setContainers((containers) => [...containers, newContainerId]);
			setItems((items) => ({
				...items,
				[newContainerId]: [
					createDefaultItem(baseLanguage, language)
				],
			}));
		});
	}

	function handleRemoveColumn() {
		// dynamically get second to last, and last column for when product wants more columns
		const [secondToLastColumnId, lastColumnId] = containers.slice(-2);
		if (!secondToLastColumnId || !lastColumnId) return;

		unstable_batchedUpdates(() => {
			setContainers((containers) => containers.slice(0, containers.length - 1));
			// move any of the deleted column items into the first column at the bottom
			setItems((columns) => {
				const itemsToMove = columns[Number(lastColumnId)];

				const cards = [...columns[Number(secondToLastColumnId)], ...itemsToMove];
				const _columns = { [Number(secondToLastColumnId)]: cards };
				return _columns;
			});
		});
		setIsEdited(true);
	}

	function getNextContainerId() {
		const containerIds = Object.keys(items).map(Number);
		const maxNum = Math.max(...containerIds);
		const nextNum = maxNum + 1;
		return nextNum;
	}
});

EditorAccordion.displayName = 'EditorAccordion';

export default EditorAccordion;
